import React, { useState, useEffect, useCallback, useRef } from 'react';
import { API } from '../../App';
import {
  Shield, Search, Download, RefreshCw, ChevronLeft, ChevronRight,
  CheckCircle, AlertTriangle, XCircle, Filter, Calendar, FileText,
  Link2, Loader2, X, Copy, Check
} from 'lucide-react';

/* --- Audit Event Detail Flyout (#580) --- */
function AuditEventFlyout({ event, onClose }) {
  const [copied, setCopied] = useState(false);
  const flyoutRef = useRef(null);

  // ESC key closes flyout
  useEffect(() => {
    if (!event) return;
    const handleKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [event, onClose]);

  if (!event) return null;

  // #584: Ensure details is an object (may be a JSON string from some DB paths)
  const details = typeof event.details === 'string'
    ? (() => { try { return JSON.parse(event.details); } catch { return {}; } })()
    : (event.details || {});
  const eventWithDetails = { ...event, details };

  const rawJson = JSON.stringify(details, null, 2);
  const handleCopy = () => {
    navigator.clipboard.writeText(rawJson);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const formatTs = (ts) => {
    if (!ts) return '—';
    return new Date(ts).toLocaleString('en-US', {
      weekday: 'short', month: 'short', day: 'numeric', year: 'numeric',
      hour: '2-digit', minute: '2-digit', second: '2-digit', timeZoneName: 'short'
    });
  };

  const sections = [
    { label: 'Actor', items: [
      { k: 'Email', v: event.actor_email || '—' },
      { k: 'Name', v: event.actor_name || '—' },
      { k: 'User ID', v: event.actor_id || 'system' },
    ]},
    { label: 'Resource', items: [
      { k: 'Type', v: event.resource_type || '—' },
      { k: 'ID', v: event.resource_id || '—' },
    ]},
    { label: 'Context', items: [
      ...(details.ip ? [{ k: 'IP Address', v: details.ip }] : []),
      ...(details.plan ? [{ k: 'Plan', v: details.plan }] : []),
      ...(details.transport ? [{ k: 'Transport', v: details.transport }] : []),
      ...(details.session_id ? [{ k: 'Session', v: details.session_id }] : []),
    ]},
    // #582/#584: API Key section for MCP events
    ...(details.api_key_id ? [{ label: 'API Key', items: [
      { k: 'Label', v: details.api_key_name || '(unnamed)' },
      { k: 'Key ID', v: details.api_key_id },
      { k: 'Fingerprint', v: `…${details.api_key_last4 || '????'}` },
      ...(details.api_key_prefix ? [{ k: 'Prefix', v: details.api_key_prefix }] : []),
      ...(details.api_key_created_at ? [{ k: 'Created', v: formatTs(details.api_key_created_at) }] : []),
    ]}] : []),
  ];

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/40 z-40" onClick={onClose} />
      {/* Flyout */}
      <div
        ref={flyoutRef}
        className="fixed top-0 right-0 h-full w-full max-w-[480px] bg-[#141414] border-l border-[#2A2A2A] z-50 shadow-2xl overflow-y-auto animate-slide-in-right"
        style={{ animation: 'slideInRight 0.2s ease-out' }}
      >
        {/* Header */}
        <div className="sticky top-0 bg-[#141414] border-b border-[#2A2A2A] px-5 py-4 flex items-start justify-between z-10">
          <div>
            <span className="font-mono text-xs font-bold text-[#FF4D00] uppercase tracking-wider">
              {(event.action || 'Unknown').toUpperCase()}
            </span>
            <p className="text-xs font-mono text-[#A0A0A0] mt-1">
              {formatTs(event.timestamp || event.created_at)}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded hover:bg-[#2A2A2A] text-[#A0A0A0] hover:text-[#F5F1E8] transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Sections */}
        <div className="px-5 py-4 space-y-5">
          {sections.map((section) => (
            section.items.length > 0 && (
              <div key={section.label}>
                <h4 className="text-[10px] font-mono text-[#A0A0A0] uppercase tracking-widest mb-2">{section.label}</h4>
                <div className="space-y-1.5">
                  {section.items.map(({ k, v }) => (
                    <div key={k} className="flex items-baseline gap-3">
                      <span className="text-xs font-mono text-[#666] w-20 shrink-0">{k}</span>
                      <span className="text-xs font-mono text-[#F5F1E8] break-all">{v}</span>
                    </div>
                  ))}
                </div>
              </div>
            )
          ))}

          {/* Raw Details */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-[10px] font-mono text-[#A0A0A0] uppercase tracking-widest">Raw Details</h4>
              <button
                onClick={handleCopy}
                className="flex items-center gap-1 text-[10px] font-mono text-[#A0A0A0] hover:text-[#FF4D00] transition-colors"
              >
                {copied ? <Check className="w-3 h-3 text-green-400" /> : <Copy className="w-3 h-3" />}
                {copied ? 'Copied' : 'Copy'}
              </button>
            </div>
            <pre className="text-[11px] font-mono text-[#F5F1E8] bg-[#0A0A0A] border border-[#2A2A2A] rounded p-3 overflow-x-auto whitespace-pre max-h-[400px] overflow-y-auto">
              {rawJson}
            </pre>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes slideInRight {
          from { transform: translateX(100%); }
          to { transform: translateX(0); }
        }
      `}</style>
    </>
  );
}

// #740: workspaceId is now optional. When null, the component renders the
// platform-wide audit log (super-admin scope). The Enterprise Audit tab
// passes a UUID for workspace-scoped reads; the Platform Admin → Audit Log
// tab mounts this with workspaceId={null} to consolidate onto the same
// component instead of maintaining a parallel broken renderer.
export default function AuditSection({ workspaceId = null }) {
  const isPlatformScope = !workspaceId;
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [perPage] = useState(25);

  // Filters
  const [actionFilter, setActionFilter] = useState('');
  const [resourceTypeFilter, setResourceTypeFilter] = useState('');
  const [actorSearch, setActorSearch] = useState('');
  const [searchText, setSearchText] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [filtersOpen, setFiltersOpen] = useState(false);

  // #698: hide *.fray service-principal rows by default; toggle to surface them
  const [showServiceActivity, setShowServiceActivity] = useState(false);

  // Export
  const [exportFormat, setExportFormat] = useState('csv');
  const [exporting, setExporting] = useState(false);

  // Chain verification
  const [chainStatus, setChainStatus] = useState(null);
  const [verifying, setVerifying] = useState(false);

  // Selected event detail
  const [selectedEvent, setSelectedEvent] = useState(null);

  const fetchEvents = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      // #740: omit workspace_id query param when in platform scope so the
      // backend skips the workspace filter and visibility narrowing.
      if (workspaceId) params.set('workspace_id', workspaceId);
      params.set('page', page);
      params.set('limit', perPage);
      if (actionFilter) params.set('action', actionFilter);
      if (resourceTypeFilter) params.set('resource_type', resourceTypeFilter);
      if (actorSearch) params.set('actor_id', actorSearch);
      if (startDate) params.set('date_from', startDate);
      if (endDate) params.set('date_to', endDate);
      // #698: include_service controls whether *.fray service-principal rows appear
      if (showServiceActivity) params.set('include_service', 'true');

      const res = await API.get(`/api/enterprise/audit/events?${params}`);
      setEvents(res.data.events || []);
      setTotalPages(Math.ceil((res.data.total || 0) / perPage) || 1);
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    } finally {
      setLoading(false);
    }
  }, [workspaceId, page, perPage, actionFilter, resourceTypeFilter, actorSearch, startDate, endDate, showServiceActivity]);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  const fetchChainStatus = useCallback(async () => {
    // #740: chain hashing is per-workspace today. In platform scope we skip
    // the chain-status fetch entirely — separate issue would be needed to
    // retrofit platform-wide chain semantics.
    if (isPlatformScope) return;
    try {
      const res = await API.get(`/api/enterprise/audit/chain-status?workspace_id=${workspaceId}`);
      setChainStatus(res.data);
    } catch {
      // non-critical
    }
  }, [workspaceId, isPlatformScope]);

  useEffect(() => {
    fetchChainStatus();
  }, [fetchChainStatus]);

  const handleVerify = async () => {
    if (isPlatformScope) return;  // #740: chain verify is workspace-scoped
    setVerifying(true);
    try {
      const res = await API.post('/api/enterprise/audit/verify', { workspace_id: workspaceId });
      setChainStatus(res.data);
    } catch {
      setChainStatus({ valid: false, error: 'Verification request failed' });
    } finally {
      setVerifying(false);
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      // #740: omit workspace_id when in platform scope so the export covers
      // the entire audit_log table.
      const body = {
        format: exportFormat,
        filters: {}
      };
      if (workspaceId) body.workspace_id = workspaceId;
      if (startDate) body.date_from = startDate;
      if (endDate) body.date_to = endDate;
      if (actionFilter) body.filters.action = actionFilter;
      if (resourceTypeFilter) body.filters.resource_type = resourceTypeFilter;

      const res = await API.post('/api/enterprise/audit/export', body, { responseType: 'blob' });

      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `audit-log-${new Date().toISOString().split('T')[0]}.${exportFormat}`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    } finally {
      setExporting(false);
    }
  };

  const filteredEvents = searchText
    ? events.filter(e => {
        const text = searchText.toLowerCase();
        return (
          (e.action || '').toLowerCase().includes(text) ||
          (e.actor_email || e.actor_id || '').toLowerCase().includes(text) ||
          (e.resource_type || '').toLowerCase().includes(text) ||
          JSON.stringify(e.details || {}).toLowerCase().includes(text)
        );
      })
    : events;

  const formatTimestamp = (ts) => {
    if (!ts) return '—';
    const d = new Date(ts);
    return d.toLocaleString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
      hour: '2-digit', minute: '2-digit', second: '2-digit'
    });
  };

  const actionColors = {
    create: 'text-green-400',
    update: 'text-[#00D4FF]',
    delete: 'text-[#A61E17]',
    login: 'text-[#F2A900]',
    export: 'text-[#FF4D00]',
  };

  const getActionColor = (action) => {
    const key = Object.keys(actionColors).find(k => (action || '').toLowerCase().includes(k));
    return key ? actionColors[key] : 'text-[#A0A0A0]';
  };

  return (
    <div className="space-y-4">
      {/* I/O Logging banner (#564) — workspace-scoped feature, hidden in platform scope */}
      {!isPlatformScope && (
        <div className="p-3 bg-[#00D4FF]/5 border border-[#00D4FF]/20 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <FileText className="w-4 h-4 text-[#00D4FF] shrink-0" />
            <p className="text-xs text-[#A0A0A0]">
              Looking for the actual prompts and completions of frays?
              <strong className="text-[#F5F1E8]"> Enable OpenRouter Input & Output Logging</strong> in the Observatory tab.
            </p>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Shield className="w-5 h-5 text-[#FF4D00]" />
          <h2 className="text-lg font-['Barlow_Condensed'] font-bold text-[#F5F1E8] uppercase tracking-wide">
            {isPlatformScope ? 'Platform-Wide Audit Log' : 'Audit Trail'}
          </h2>
        </div>
        <div className="flex items-center gap-2">
          {/* #740: chain verification is workspace-scoped today; hide in
              platform scope until per-platform chain hashing exists. */}
          {!isPlatformScope && chainStatus && (
            <button
              onClick={handleVerify}
              disabled={verifying}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded border border-[#2A2A2A] text-xs font-mono hover:border-[#FF4D00] transition-colors"
            >
              {verifying ? (
                <Loader2 className="w-3.5 h-3.5 text-[#A0A0A0] animate-spin" />
              ) : chainStatus.valid ? (
                <CheckCircle className="w-3.5 h-3.5 text-green-400" />
              ) : (
                <AlertTriangle className="w-3.5 h-3.5 text-[#F2A900]" />
              )}
              <span className={chainStatus.valid ? 'text-green-400' : 'text-[#F2A900]'}>
                {verifying ? 'Verifying...' : chainStatus.valid ? 'Chain Valid' : 'Chain Invalid'}
              </span>
            </button>
          )}
          {!isPlatformScope && !chainStatus && (
            <button
              onClick={handleVerify}
              disabled={verifying}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded border border-[#2A2A2A] text-xs font-mono text-[#A0A0A0] hover:border-[#FF4D00] transition-colors"
            >
              {verifying ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <Link2 className="w-3.5 h-3.5" />
              )}
              {verifying ? 'Verifying...' : 'Verify Chain'}
            </button>
          )}
        </div>
      </div>

      {/* Search & Filter Bar */}
      <div className="flex items-center gap-2">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#A0A0A0]" />
          <input
            type="text"
            placeholder="Search audit events..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            className="w-full bg-[#0F0F0F] border border-[#2A2A2A] rounded px-3 py-2 pl-9 text-sm text-[#F5F5F5] font-mono focus:border-[#FF4D00] focus:outline-none transition-colors"
          />
        </div>
        <button
          onClick={() => setFiltersOpen(!filtersOpen)}
          className={`flex items-center gap-1.5 px-3 py-2 rounded border text-sm font-mono transition-colors ${
            filtersOpen ? 'border-[#FF4D00] text-[#FF4D00]' : 'border-[#2A2A2A] text-[#A0A0A0] hover:border-[#FF4D00]'
          }`}
        >
          <Filter className="w-4 h-4" />
          Filters
        </button>
        <button
          onClick={() => { setSelectedEvent(null); fetchEvents(); }}
          disabled={loading}
          className="flex items-center gap-1.5 px-3 py-2 rounded border border-[#2A2A2A] text-sm font-mono text-[#A0A0A0] hover:border-[#FF4D00] hover:text-[#F5F1E8] transition-colors disabled:opacity-50"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
        <div className="flex items-center gap-1 border border-[#2A2A2A] rounded">
          <select
            value={exportFormat}
            onChange={(e) => setExportFormat(e.target.value)}
            className="bg-[#0F0F0F] border-none text-sm text-[#A0A0A0] font-mono px-2 py-2 focus:outline-none rounded-l"
          >
            <option value="csv">CSV</option>
            <option value="json">JSON</option>
          </select>
          <button
            onClick={handleExport}
            disabled={exporting}
            className="flex items-center gap-1.5 px-3 py-2 bg-[#FF4D00] text-[#0A0A0A] font-bold text-sm rounded-r hover:bg-[#E63E00] transition-colors disabled:opacity-50"
          >
            {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
            Export
          </button>
        </div>
      </div>

      {/* Filter Panel */}
      {filtersOpen && (
        <div className="bg-[#1A1A1A] border border-[#2A2A2A] rounded-lg p-4 grid grid-cols-2 md:grid-cols-4 gap-3">
          <div>
            <label className="block text-xs font-mono text-[#A0A0A0] mb-1">Start Date</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => { setStartDate(e.target.value); setPage(1); }}
              className="w-full bg-[#0F0F0F] border border-[#2A2A2A] rounded px-3 py-2 text-sm text-[#F5F5F5] font-mono focus:border-[#FF4D00] focus:outline-none"
            />
          </div>
          <div>
            <label className="block text-xs font-mono text-[#A0A0A0] mb-1">End Date</label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => { setEndDate(e.target.value); setPage(1); }}
              className="w-full bg-[#0F0F0F] border border-[#2A2A2A] rounded px-3 py-2 text-sm text-[#F5F5F5] font-mono focus:border-[#FF4D00] focus:outline-none"
            />
          </div>
          <div>
            <label className="block text-xs font-mono text-[#A0A0A0] mb-1">Action Type</label>
            <select
              value={actionFilter}
              onChange={(e) => { setActionFilter(e.target.value); setPage(1); }}
              className="w-full bg-[#0F0F0F] border border-[#2A2A2A] rounded px-3 py-2 text-sm text-[#F5F5F5] font-mono focus:border-[#FF4D00] focus:outline-none"
            >
              <option value="">All Actions</option>
              <option value="create">Create</option>
              <option value="update">Update</option>
              <option value="delete">Delete</option>
              <option value="login">Login</option>
              <option value="logout">Logout</option>
              <option value="export">Export</option>
              <option value="invite">Invite</option>
              <option value="role_change">Role Change</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-mono text-[#A0A0A0] mb-1">Resource Type</label>
            <select
              value={resourceTypeFilter}
              onChange={(e) => { setResourceTypeFilter(e.target.value); setPage(1); }}
              className="w-full bg-[#0F0F0F] border border-[#2A2A2A] rounded px-3 py-2 text-sm text-[#F5F5F5] font-mono focus:border-[#FF4D00] focus:outline-none"
            >
              <option value="">All Resources</option>
              <option value="user">User</option>
              <option value="workspace">Workspace</option>
              <option value="debate">Debate</option>
              <option value="template">Template</option>
              <option value="settings">Settings</option>
            </select>
          </div>
          {/* #698: toggle to surface *.fray service-principal rows */}
          <div className="col-span-2 md:col-span-2 flex items-center gap-2">
            <input
              type="checkbox"
              id="show-service-activity"
              checked={showServiceActivity}
              onChange={(e) => { setShowServiceActivity(e.target.checked); setPage(1); }}
              className="accent-[#FF4D00]"
            />
            <label
              htmlFor="show-service-activity"
              className="text-xs font-mono text-[#A0A0A0] cursor-pointer select-none"
              title="Include rows attributed to *.fray service principals (CC, deploy pipelines, system jobs). Hidden by default."
            >
              Show service activity (.fray)
            </label>
          </div>
          <div className="col-span-2 md:col-span-2 flex justify-end">
            <button
              onClick={() => {
                setActionFilter(''); setResourceTypeFilter(''); setActorSearch('');
                setStartDate(''); setEndDate(''); setSearchText('');
                setShowServiceActivity(false); setPage(1);
              }}
              className="text-xs font-mono text-[#A0A0A0] hover:text-[#FF4D00] transition-colors"
            >
              Clear all filters
            </button>
          </div>
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="bg-[#1A1A1A] border border-[#A61E17] rounded-lg p-3 flex items-center gap-2">
          <XCircle className="w-4 h-4 text-[#A61E17] flex-shrink-0" />
          <span className="text-sm text-[#F5F1E8] font-mono">{error}</span>
          <button onClick={fetchEvents} className="ml-auto text-xs text-[#FF4D00] hover:underline font-mono">Retry</button>
        </div>
      )}

      {/* Table */}
      <div className="bg-[#1A1A1A] border border-[#2A2A2A] rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[800px]">
            <thead>
              <tr className="border-b border-[#2A2A2A]">
                <th className="text-left px-4 py-3 text-xs font-mono text-[#A0A0A0] uppercase tracking-wider whitespace-nowrap min-w-[180px]">Timestamp</th>
                <th className="text-left px-4 py-3 text-xs font-mono text-[#A0A0A0] uppercase tracking-wider whitespace-nowrap min-w-[200px]">Actor</th>
                <th className="text-left px-4 py-3 text-xs font-mono text-[#A0A0A0] uppercase tracking-wider whitespace-nowrap min-w-[180px]">Action</th>
                <th className="text-left px-4 py-3 text-xs font-mono text-[#A0A0A0] uppercase tracking-wider whitespace-nowrap min-w-[140px]">Resource</th>
                <th className="text-left px-4 py-3 text-xs font-mono text-[#A0A0A0] uppercase tracking-wider min-w-[300px]">Details</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={5} className="text-center py-12">
                    <Loader2 className="w-6 h-6 text-[#FF4D00] animate-spin mx-auto mb-2" />
                    <span className="text-sm font-mono text-[#A0A0A0]">Loading audit events...</span>
                  </td>
                </tr>
              ) : filteredEvents.length === 0 ? (
                <tr>
                  <td colSpan={5} className="text-center py-12">
                    <FileText className="w-6 h-6 text-[#A0A0A0] mx-auto mb-2" />
                    <span className="text-sm font-mono text-[#A0A0A0]">No audit events found</span>
                  </td>
                </tr>
              ) : (
                filteredEvents.map((event, i) => (
                  <tr
                    key={event.id || i}
                    onClick={() => setSelectedEvent(selectedEvent?.id === event.id ? null : event)}
                    className={`border-b border-[#2A2A2A]/50 cursor-pointer transition-colors ${
                      selectedEvent?.id === event.id
                        ? 'bg-[#FF4D00]/8 border-l-[3px] border-l-[#FF4D00]'
                        : 'hover:bg-[#1E1E1E]'
                    }`}
                  >
                    <td className="px-4 py-3 font-mono text-xs text-[#A0A0A0] whitespace-nowrap">
                      {formatTimestamp(event.timestamp || event.created_at)}
                    </td>
                    <td className="px-4 py-3 font-mono text-xs text-[#F5F1E8]" title={event.actor_id || ''}>
                      {event.actor_email || event.actor_name || (event.actor_id ? event.actor_id.slice(0, 8) + '...' : 'system')}
                      {/* #698: visual hint that this row is a pre-migration historical backfill */}
                      {event.is_pre_migration && (
                        <span className="ml-2 text-[#666] text-[10px] uppercase tracking-wider" title="Pre-migration historical row — actor_email reconstructed from users.email at backfill time, not captured at write time">[legacy]</span>
                      )}
                      {/* #698: under impersonation, render "actor → target" */}
                      {event.actor_on_behalf_of_email && (
                        <span className="ml-1 text-[#A0A0A0] text-[11px]">
                          → <span className="text-[#00D4FF]">{event.actor_on_behalf_of_email}</span>
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`font-mono text-xs font-semibold ${getActionColor(event.action)}`}>
                        {(event.action || '—').toUpperCase()}
                      </span>
                    </td>
                    <td className="px-4 py-3 font-mono text-xs text-[#00D4FF]">
                      {event.resource_type || '—'}
                      {event.resource_id && (
                        <span className="text-[#A0A0A0] ml-1">#{event.resource_id.slice(0, 8)}</span>
                      )}
                    </td>
                    <td className="px-4 py-3 font-mono text-xs text-[#A0A0A0]">
                      {event.details
                        ? typeof event.details === 'string'
                          ? event.details
                          : JSON.stringify(event.details).slice(0, 120)
                        : '—'}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Event Detail Flyout (#580) */}
        <AuditEventFlyout event={selectedEvent} onClose={() => setSelectedEvent(null)} />

        {/* Pagination */}
        {!loading && filteredEvents.length > 0 && (
          <div className="border-t border-[#2A2A2A] px-4 py-3 flex items-center justify-between">
            <span className="text-xs font-mono text-[#A0A0A0]">
              Page {page} of {totalPages}
            </span>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page <= 1}
                className="p-1.5 rounded border border-[#2A2A2A] text-[#A0A0A0] hover:border-[#FF4D00] hover:text-[#F5F1E8] disabled:opacity-30 disabled:hover:border-[#2A2A2A] transition-colors"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <button
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages}
                className="p-1.5 rounded border border-[#2A2A2A] text-[#A0A0A0] hover:border-[#FF4D00] hover:text-[#F5F1E8] disabled:opacity-30 disabled:hover:border-[#2A2A2A] transition-colors"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
              <button
                onClick={fetchEvents}
                className="p-1.5 rounded border border-[#2A2A2A] text-[#A0A0A0] hover:border-[#FF4D00] hover:text-[#F5F1E8] transition-colors ml-2"
              >
                <RefreshCw className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
